import { useTranslation } from 'react-i18next';
import { Route, Switch, useParams } from 'react-router-dom';
import useCan from 'hooks/useCan';
import { KizenError } from 'services/errors';
import { StyledPage } from './styles';
import { PageSizing } from '__components/Layout/PageContentWidth';
import { ContentWidth } from '__components/Layout/PageContentWidth';
import { Nav } from 'components/Navigation/NavLayout';
import { useBreakpoint } from 'app/spacing';
import { MobileFallback } from 'pages/Common/MobileFallback';
import { DesktopOnlyButton } from '__pages/Common/styles/ControlBar';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { Catalogs } from './subpages/Catalogs';
import { CatalogDetail } from './subpages/CatalogDetail';
import { DeleteCatalogButton } from './subpages/CatalogDetail/DeleteCatalogButton';
import {
  CatalogToolbarProvider,
  useSaveAction,
} from './subpages/CatalogDetail/ToolbarContext';
import {
  CatalogDetailProvider,
  useCatalogDetailPage,
} from './subpages/CatalogDetail/context';

const Navigation = () => {
  const { t } = useTranslation();
  const { subpage } = useParams<{ subpage?: string }>();
  const saveAction = useSaveAction();
  const { catalog, subroutes } = useCatalogDetailPage();
  const name = catalog?.name ?? '';

  return (
    <Nav.Container>
      <Nav.TopNavRow>
        <Nav.TopNavLeftContainer>
          <Nav.TopNavBackButton
            path="/knowledge"
            label={t('back to knowledge catalogs')}
          />
        </Nav.TopNavLeftContainer>
        <Nav.TopNavCenterContainer>
          <Nav.TopNavHeader label={name} />
        </Nav.TopNavCenterContainer>
        <Nav.TopNavRightContainer>
          {saveAction ? (
            <>
              <DeleteCatalogButton />
              <DesktopOnlyButton
                onClick={saveAction.onSave}
                disabled={saveAction.disabled}
                data-qa-id="catalog-settings-save-button"
              >
                {t('Save')}
              </DesktopOnlyButton>
            </>
          ) : null}
        </Nav.TopNavRightContainer>
      </Nav.TopNavRow>
      <Nav.SubNavigationRow>
        {subroutes.map((route, i, arr) => (
          <Nav.SubNavigationLink
            key={route.path}
            route={route}
            isLast={i === arr.length - 1}
            numberOfLinks={arr.length}
            params={{ subpage }}
          />
        ))}
      </Nav.SubNavigationRow>
    </Nav.Container>
  );
};

export const KnowledgeSettingsPage = () => {
  const { t } = useTranslation();

  const settingsAccess = useCan({
    view: 'sections',
    for: 'settings_section.knowledgebase_setting',
  });

  if (!settingsAccess) {
    throw new KizenError(404);
  }

  useSetTitleOnLoad(t('Knowledge Library'));

  const isMobile = useBreakpoint();

  return (
    <CatalogDetailProvider>
      <CatalogToolbarProvider>
        <StyledPage>
          <Route
            path="/knowledge/:catalogId/:subpage?"
            render={() => <Navigation />}
          />
          <PageSizing>
            <ContentWidth>
              <MobileFallback isMobile={isMobile}>
                <Switch>
                  <Route exact path="/knowledge" render={() => <Catalogs />} />
                  <Route
                    path="/knowledge/:catalogId/:subpage?"
                    render={() => <CatalogDetail />}
                  />
                </Switch>
              </MobileFallback>
            </ContentWidth>
          </PageSizing>
        </StyledPage>
      </CatalogToolbarProvider>
    </CatalogDetailProvider>
  );
};
